/**
 * Visible checkpoint gates.
 *
 * A `Checkpoint` (see src/types.ts) is a plane across the track — invisible
 * geometry the lap counter tests against. That is fine for the maths and hopeless
 * for the driver, who otherwise has no idea a gate exists until the finish line
 * refuses to count the lap. So every checkpoint gets a physical gate: two lit
 * pylons just outside the kerbs and a translucent curtain of light between them.
 *
 * The gates are *state driven*, and that state is the same one the HUD pips and
 * the minimap ticks show:
 *
 *   ahead   dim cyan     — somewhere later this lap
 *   next    bright amber — the one to drive through, breathing so it draws the eye
 *   passed  calm green   — collected
 *   missed  red, blinking hard — driven around; go back for it or lose the lap
 *
 * Colours are carried by unlit materials, so a gate reads the same under the
 * sunset, the desert noon and the neon night without any per-map tuning.
 *
 * Geometry conventions match track.ts: y = 0 is the ground and a gate faces
 * along the direction of travel through it.
 */
import * as THREE from 'three';

import type { Checkpoint, CheckpointMarkers } from './types';
import type { CheckpointState } from './progress';

/** Height of the pylons either side of the road. */
const PYLON_HEIGHT = 5.4;
/** Height of the curtain of light spanning them. */
const CURTAIN_HEIGHT = 3.8;

/** Per-state look: curtain tint, how solid it sits, and the pylon lamp colour. */
interface StateLook {
  color: number;
  opacity: number;
  /** Amplitude of the idle breathing, as a fraction of `opacity`. */
  pulse: number;
  /** Breaths per second. */
  rate: number;
}

/**
 * Opacities are deliberately low. A gate is passed at a couple of metres from
 * the camera, and anything solid enough to read as a banner from 100 m away
 * blanks the entire windscreen at the moment it actually matters — which is
 * precisely the moment the driver is picking a line through it.
 */
const LOOKS: Record<CheckpointState, StateLook> = {
  ahead: { color: 0x4ad9ff, opacity: 0.11, pulse: 0.15, rate: 0.5 },
  next: { color: 0xffcf33, opacity: 0.26, pulse: 0.45, rate: 1.6 },
  passed: { color: 0x53e08a, opacity: 0.07, pulse: 0.0, rate: 0 },
  missed: { color: 0xff3b3b, opacity: 0.42, pulse: 0.6, rate: 3.4 },
};

/**
 * Vertical alpha ramp for the curtain: strongest near the ground, gone by the
 * top. A hard-edged rectangle of colour hanging over the road reads as a wall;
 * fading it out makes it read as something to drive through.
 */
function createCurtainTexture(): THREE.DataTexture {
  const height = 64;
  const data = new Uint8Array(height * 4);
  for (let y = 0; y < height; y++) {
    const t = y / (height - 1); // 0 at the bottom row, 1 at the top
    const alpha = (1 - t) ** 2.2;
    const i = y * 4;
    data[i] = 255;
    data[i + 1] = 255;
    data[i + 2] = 255;
    data[i + 3] = Math.round(alpha * 255);
  }
  const tex = new THREE.DataTexture(data, 1, height, THREE.RGBAFormat);
  tex.wrapS = THREE.ClampToEdgeWrapping;
  tex.wrapT = THREE.ClampToEdgeWrapping;
  tex.magFilter = THREE.LinearFilter;
  tex.minFilter = THREE.LinearFilter;
  tex.generateMipmaps = false;
  tex.colorSpace = THREE.NoColorSpace;
  tex.needsUpdate = true;
  return tex;
}

/** One gate's mutable parts. */
interface Gate {
  state: CheckpointState;
  curtain: THREE.MeshBasicMaterial;
  /** The line painted across the tarmac at the gate. */
  strip: THREE.MeshBasicMaterial;
  /** Pylon caps — the parts that read as lamps. */
  lamps: THREE.MeshBasicMaterial[];
  /** The curtain mesh, scaled up briefly when the gate is crossed. */
  mesh: THREE.Mesh;
  /** 1 right after a crossing, decaying to 0. */
  flash: number;
}

/**
 * Build a gate at every checkpoint.
 *
 * Gates are drawn barrier to barrier, because that is exactly how wide the rule
 * is: a `Checkpoint` counts anywhere a kart can physically drive. Drawing them
 * only as wide as the tarmac would quietly lie to anyone running through the
 * runoff, who would pass outside the posts and still be credited.
 */
export function createCheckpointMarkers(
  checkpoints: Checkpoint[],
  wallRadius: number,
): CheckpointMarkers {
  const group = new THREE.Group();
  group.name = 'checkpoints';

  const span = wallRadius * 2;
  const curtainTex = createCurtainTexture();

  // Shared, stateless parts: one geometry and one material each, however many
  // gates the circuit has.
  const postGeo = new THREE.BoxGeometry(0.5, PYLON_HEIGHT, 0.5);
  postGeo.translate(0, PYLON_HEIGHT / 2, 0);
  const capGeo = new THREE.BoxGeometry(1.5, 0.38, 0.9);
  const curtainGeo = new THREE.PlaneGeometry(span, CURTAIN_HEIGHT);
  curtainGeo.translate(0, CURTAIN_HEIGHT / 2, 0);
  const stripGeo = new THREE.PlaneGeometry(span, 0.55);
  stripGeo.rotateX(-Math.PI / 2);
  const postMat = new THREE.MeshStandardMaterial({
    color: 0x20242f,
    roughness: 0.45,
    metalness: 0.5,
  });

  const gates: Gate[] = [];

  for (const cp of checkpoints) {
    const gate = new THREE.Group();
    gate.name = `checkpoint-${cp.index}`;
    gate.position.set(cp.position.x, 0, cp.position.z);
    gate.rotation.y = Math.atan2(cp.forward.x, cp.forward.z);

    const lamps: THREE.MeshBasicMaterial[] = [];
    const lampMat = (): THREE.MeshBasicMaterial => {
      const mat = new THREE.MeshBasicMaterial({ color: 0xffffff });
      lamps.push(mat);
      return mat;
    };

    // The start/finish line is already the most heavily dressed part of the
    // circuit — gantry, lights, chequers — and it is where the whole grid sits
    // at lights-out. It gets the painted strip and nothing that stands up.
    const isStartLine = cp.index === 0;

    if (!isStartLine) {
      for (const side of [-1, 1]) {
        const post = new THREE.Mesh(postGeo, postMat);
        post.position.x = (span / 2) * side;
        post.castShadow = true;
        gate.add(post);

        const cap = new THREE.Mesh(capGeo, lampMat());
        cap.position.set((span / 2) * side, PYLON_HEIGHT + 0.19, 0);
        gate.add(cap);
      }
    }

    // A line painted across the tarmac. It carries the state for the last few
    // metres, once the curtain has gone past the camera — kept thin and washed
    // over the road rather than laid on top of it, so it never hides the
    // chequers at the start/finish line.
    const stripMat = new THREE.MeshBasicMaterial({
      color: 0xffffff,
      transparent: true,
      depthWrite: false,
    });
    const strip = new THREE.Mesh(stripGeo, stripMat);
    strip.position.y = 0.09;
    strip.renderOrder = 1;
    gate.add(strip);

    const curtainMat = new THREE.MeshBasicMaterial({
      map: curtainTex,
      transparent: true,
      depthWrite: false,
      side: THREE.DoubleSide,
    });
    const curtain = new THREE.Mesh(curtainGeo, curtainMat);
    // Draw after the opaque world so the curtain blends over the road behind it.
    curtain.renderOrder = 2;
    curtain.visible = !isStartLine;
    gate.add(curtain);

    group.add(gate);
    gates.push({
      state: 'ahead',
      curtain: curtainMat,
      strip: stripMat,
      lamps,
      mesh: curtain,
      flash: 0,
    });
  }

  let time = 0;

  /** Push one gate's state into its materials, including the idle breathing. */
  const paint = (gate: Gate): void => {
    const look = LOOKS[gate.state];
    const breath = look.rate > 0 ? Math.sin(time * look.rate * Math.PI * 2) * 0.5 + 0.5 : 0;
    const lift = look.pulse * breath + gate.flash * 0.9;

    gate.curtain.color.setHex(look.color);
    gate.curtain.opacity = Math.min(1, look.opacity * (1 + lift));
    // A crossing briefly stretches the curtain, which reads as it being knocked
    // out of the way rather than simply flickering.
    const stretch = 1 + gate.flash * 0.35;
    gate.mesh.scale.set(1, stretch, 1);

    gate.strip.color.setHex(look.color);
    gate.strip.opacity = Math.min(0.8, look.opacity * 2.6 * (1 + lift));

    const lampBoost = 0.55 + lift * 0.7;
    for (const lamp of gate.lamps) {
      lamp.color.setHex(look.color).multiplyScalar(lampBoost);
    }
  };

  const markers: CheckpointMarkers = {
    group,

    setStates(states: readonly CheckpointState[]): void {
      for (let i = 0; i < gates.length; i++) {
        const next = states[i];
        if (next === undefined || next === gates[i].state) continue;
        gates[i].state = next;
        paint(gates[i]);
      }
    },

    pulse(index: number): void {
      const gate = gates[index];
      if (gate) gate.flash = 1;
    },

    update(dt: number): void {
      time += dt;
      for (const gate of gates) {
        if (gate.flash > 0) {
          gate.flash = Math.max(0, gate.flash - dt * 2.2);
        } else if (LOOKS[gate.state].rate === 0) {
          continue; // static state, nothing to animate
        }
        paint(gate);
      }
    },
  };

  for (const gate of gates) paint(gate);
  return markers;
}
